// Notification preferences — per-category on/off toggles, backed by the framework's
// /api/notifications/preferences (opt-out: default on). Shown in Kontoinställningar.
import React, { useEffect, useState } from 'react';
import { Bell, Volume2, Play } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getNotificationApi } from '@/lib/api/client';
import { Switch } from '@/components/ui/switch';
import { IconChip } from '@/components/custom-ui';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import { isNotificationSoundEnabled, setNotificationSoundEnabled, playNotificationChime } from '@/lib/notificationSound';

// Categories must match the backend constants in services/notification_emit.go.
const CATEGORIES: { key: string; label: string; desc: string; roles: string[] }[] = [
    { key: 'applications', label: 'Ansökningar', desc: 'Statusändringar på ansökningar', roles: ['candidate', 'employer'] },
    { key: 'invitations', label: 'Inbjudningar', desc: 'Inbjudningar och svar', roles: ['candidate', 'employer'] },
    { key: 'onboarding', label: 'Onboarding', desc: 'Tilldelade moduler och slutföranden', roles: ['candidate', 'employer'] },
    { key: 'postings', label: 'Bevakningar', desc: 'Nya annonser som matchar dina sparade sökningar', roles: ['candidate'] },
    { key: 'workplace', label: 'Arbetsplats', desc: 'Anställning och din enhet', roles: ['candidate'] },
    { key: 'admin', label: 'Systemaviseringar', desc: 'Kontaktförfrågningar m.m.', roles: ['admin'] },
];

export const NotificationPreferences: React.FC = () => {
    const { user } = useAuth();
    const [enabled, setEnabled] = useState<Record<string, boolean>>({});
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getNotificationApi().getPreferences().then((prefs) => {
            if (cancelled) return;
            const map: Record<string, boolean> = {};
            prefs.forEach((p) => { map[p.category] = p.enabled; });
            setEnabled(map);
            setLoaded(true);
        }).catch(() => setLoaded(true));
        return () => { cancelled = true; };
    }, []);

    const role = user?.role ?? 'candidate';
    const cats = CATEGORIES.filter((c) => c.roles.includes(role));
    const isOn = (key: string) => enabled[key] !== false; // default on (opt-out)

    const toggle = (key: string, on: boolean) => {
        setEnabled((prev) => ({ ...prev, [key]: on }));
        getNotificationApi().setPreference(key, on).catch(() => {
            setEnabled((prev) => ({ ...prev, [key]: !on })); // revert on error
        });
    };

    // Sound is a per-device UI preference (localStorage), not a server-side
    // delivery preference — so it's always shown, regardless of role.
    const [soundOn, setSoundOn] = useState(isNotificationSoundEnabled());
    const toggleSound = (on: boolean) => {
        setSoundOn(on);
        setNotificationSoundEnabled(on);
        if (on) playNotificationChime(true); // preview so the user hears what they enabled
    };

    return (
        <div className={NEXILINK_CARD_WHITE_ELEVATED}>
            <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <IconChip icon={Bell} size="lg" />
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900">Notiser</h2>
                        <p className="text-sm text-gray-600">Välj vilka aviseringar du vill få i appen.</p>
                    </div>
                </div>
            </div>
            <div className="divide-y divide-gray-100">
                {cats.map((c) => (
                    <div key={c.key} className="flex items-center justify-between p-4 sm:px-6">
                        <div className="pr-4">
                            <p className="text-sm font-medium text-gray-900">{c.label}</p>
                            <p className="text-xs text-gray-500">{c.desc}</p>
                        </div>
                        <Switch
                            checked={isOn(c.key)}
                            onCheckedChange={(v) => toggle(c.key, v)}
                            disabled={!loaded}
                        />
                    </div>
                ))}

                {/* Per-device sound preference (localStorage), not a delivery pref. */}
                <div className="flex items-center justify-between p-4 sm:px-6">
                    <div className="pr-4 flex items-start gap-2.5">
                        <Volume2 className="h-4 w-4 text-[#1A4D4F] mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-gray-900">Ljud vid nya notiser</p>
                            <p className="text-xs text-gray-500">Spela ett diskret ljud när en ny notis kommer. Gäller endast den här enheten.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => playNotificationChime(true)}
                            className="flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:text-[#143c3e] hover:underline"
                            title="Spela upp ljudet"
                        >
                            <Play className="h-3.5 w-3.5" /> Testa
                        </button>
                        <Switch checked={soundOn} onCheckedChange={toggleSound} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationPreferences;
